// ABOUTME: Email verification countdown ribbon for unverified users during grace period
// ABOUTME: Shows days remaining and resend link with frontend rate limiting (5 minutes)

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Button, Typography, Link } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { resendVerificationEmail } from '../services/api';
import { calculateDaysRemaining, shouldShowVerificationRibbon } from '../utils/emailVerification';
import { matchesRoutePattern } from '../utils/routeUtils';
import { useSiteSettings } from '../hooks/useSiteSettings';

// Routes where this ribbon should be suppressed (Issue #1234)
const SUPPRESSED_ROUTES = [
  '/setup',
  '/family-setup',
  '/invited-user-onboarding',
  '/verify-email',
];

interface EmailVerificationRibbonProps {
  currentPath?: string;
}

const EmailVerificationRibbon: React.FC<EmailVerificationRibbonProps> = ({ currentPath }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { data: siteSettings } = useSiteSettings();
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining
  const [message, setMessage] = useState('');

  // Calculate days remaining on mount and when user changes
  useEffect(() => {
    if (user?.createdAt) {
      const days = calculateDaysRemaining(user.createdAt);
      setDaysRemaining(days);
    }
  }, [user]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!user?.email || resendCooldown > 0) return;

    setIsResending(true);
    setMessage('');

    try {
      await resendVerificationEmail(user.email);
      setMessage('Email sent! Check your inbox.');
      setResendCooldown(300); // 5 minutes = 300 seconds
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setMessage('Please wait before requesting another email.');
      } else {
        setMessage('Failed to send email. Please try again later.');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Check if ribbon should be suppressed based on current route
  if (currentPath && matchesRoutePattern(currentPath, SUPPRESSED_ROUTES)) {
    return null;
  }

  // Don't show ribbon if user is verified or grace period expired
  const requireEmailVerification = siteSettings?.requireEmailVerification ?? true;
  if (!shouldShowVerificationRibbon(user, requireEmailVerification)) {
    return null;
  }

  const formatCooldown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      data-testid="email-verification-ribbon"
      role="banner"
      sx={{
        width: '100%',
        backgroundColor: 'info.main',
        color: 'info.contrastText',
        py: 1.5,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body1" fontWeight="medium">
        Please verify your email - {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
      </Typography>

      {resendCooldown > 0 ? (
        <Button
          variant="outlined"
          size="small"
          disabled
          sx={{
            color: 'info.contrastText',
            borderColor: 'info.contrastText',
            '&.Mui-disabled': {
              color: alpha(theme.palette.common.white, 0.5),
              borderColor: alpha(theme.palette.common.white, 0.3),
            },
          }}
        >
          Resend verification ({formatCooldown(resendCooldown)})
        </Button>
      ) : (
        <Link
          component="button"
          onClick={handleResendClick}
          disabled={isResending}
          sx={{
            color: 'info.contrastText',
            textDecorationColor: 'info.contrastText',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': {
              textDecorationColor: 'info.contrastText',
            },
          }}
        >
          {isResending ? 'Sending...' : 'Resend verification'}
        </Link>
      )}

      {message && (
        <Typography variant="body2" sx={{ ml: 2 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default EmailVerificationRibbon;
